import React from 'react';
import styled from 'styled-components';

import { formatNumber, roundNumberAtDecimals } from 'lib/display/formatting';
import { useSelectMarketTickSize } from 'reduxState/markets/listed/listedMarketsUtils';

import { ChartMousePosition } from './types';

export const tooltipStyle = {
  height: 56,
  width: 200,
  bid: { color: 'var(--green4)' },
  ask: { color: 'var(--red3)' },
};

const Wrap = styled.div<{ $color: string }>`
  position: absolute;
  user-select: none;
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) => $color};
  background: var(--lightBlack);

  display: flex;
  align-items: center;
  padding: 0 16px;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  width: ${tooltipStyle.width}px;
  height: ${tooltipStyle.height}px;

  > div {
    width: 100%;
  }
`;

const Pair = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
`;

const Label = styled.div`
  color: var(--white);
  font-size: 12px;
`;

const Value = styled.div`
  font-size: 12px;
`;

type TooltipProps = {
  mousePosition: ChartMousePosition;
  baseSymbol: string;
  quoteSymbol: string;
  offsetX: number;
  offsetY: number;
  xScaled?: number;
  yScaled?: number;
};

export default function MarketDepthTooltip({
  mousePosition,
  xScaled,
  yScaled,
  offsetX,
  offsetY,
  baseSymbol,
  quoteSymbol,
}: TooltipProps) {
  const currentMarketTickSize = useSelectMarketTickSize(
    `${baseSymbol}-${quoteSymbol}`
  );

  if (!mousePosition || !xScaled || !yScaled) {
    return null;
  }
  const x = xScaled - (mousePosition.type === 'bid' ? 0 : tooltipStyle.width);
  const y = yScaled - tooltipStyle.height - 5;

  const { color } =
    mousePosition.type === 'bid' ? tooltipStyle.bid : tooltipStyle.ask;

  return (
    <Wrap
      $color={color}
      style={{
        left: x + offsetX,
        top: y + offsetY,
        borderBottomLeftRadius: mousePosition.type !== 'bid' ? 5 : 0,
        borderBottomRightRadius: mousePosition.type === 'bid' ? 5 : 0,
      }}
    >
      <div>
        <Pair>
          <Label>Price:</Label>{' '}
          <Value>
            {currentMarketTickSize &&
              roundNumberAtDecimals(
                mousePosition.xValue,
                currentMarketTickSize?.visibleDecimals
              )}{' '}
            {quoteSymbol}
          </Value>
        </Pair>
        <Pair>
          <Label>Quantity:</Label>{' '}
          <Value>
            {formatNumber(mousePosition.cumulativeCount, {
              totalDigits: 8,
            })}
          </Value>
        </Pair>
      </div>
    </Wrap>
  );
}
